import { RiskLevel } from '@equip.health/ui';

import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyNameToSurveyKeyMap } from '~/lib/constants/survey/SurveyNameToSurveyKeyMap';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import {
  deriveBstadRiskLevel,
  deriveCrafftRiskLevel,
  deriveNiaaaRiskLevel,
} from '~/lib/util/assessmentListScoreStrategies.util';
import { sumSpecificKeys } from '~/lib/util/survey.util';
import {
  calculateCssrsLifetimeScoreAndRiskLabel,
  calculateCssrsSlcScreenRisk,
} from '~/lib/util/surveyBadge.util';

export const AssessmentListDataScoreStrategyMap: Partial<
  Record<
    SurveyName,
    {
      deriveScores: (assessment: SurveyResponse) => {
        scores?: (number | null)[];
        riskCategories?: (RiskLevel | null)[];
      };
    }
  >
> = {
  [SurveyName.NIAAA_BSTAD]: {
    deriveScores: (assessment: SurveyResponse) => {
      if (assessment.status !== SurveyResponseStatus.DONE) {
        return {
          riskCategories: [null, null, null],
        };
      }

      const resultsObj = JSON.parse(assessment.jsonResults);
      return {
        riskCategories: [
          deriveNiaaaRiskLevel(resultsObj),
          deriveBstadRiskLevel(resultsObj),
          deriveCrafftRiskLevel(resultsObj),
        ],
      };
    },
  },
  [SurveyName.C_SSRS_LIFETIME]: {
    deriveScores: (assessment: SurveyResponse) => {
      if (assessment.status !== SurveyResponseStatus.DONE) {
        return {
          scores: [null, null],
          riskCategories: [null, null],
        };
      }
      const resultsObj = JSON.parse(assessment.jsonResults);
      const result = calculateCssrsLifetimeScoreAndRiskLabel(resultsObj);
      return {
        scores: [result.lifetime.score, result.pastMonth.score],
        riskCategories: [result.lifetime.risk, result.pastMonth.risk],
      };
    },
  },
  [SurveyName.C_SSRS_SLC_SCREENER]: {
    deriveScores: (assessment: SurveyResponse) => {
      if (assessment.status !== SurveyResponseStatus.DONE) {
        return {
          riskCategories: [null],
        };
      }
      return {
        riskCategories: [
          calculateCssrsSlcScreenRisk(JSON.parse(assessment.jsonResults)),
        ],
      };
    },
  },
  [SurveyName.BAS]: {
    deriveScores: (assessment: SurveyResponse) => {
      if (assessment.status !== SurveyResponseStatus.DONE) {
        return {
          scores: [null, null],
        };
      }
      const qKey = SurveyNameToSurveyKeyMap[SurveyName.BAS];
      const objectiveScore = sumSpecificKeys(assessment.jsonResults, [
        `${qKey}-1`,
        `${qKey}-2`,
        `${qKey}-3`,
        `${qKey}-4`,
        `${qKey}-5`,
        `${qKey}-6`,
        `${qKey}-7`,
        `${qKey}-8`,
        `${qKey}-9`,
        `${qKey}-10`,
      ]);

      const subjectiveScore = sumSpecificKeys(assessment.jsonResults, [
        `${qKey}-11`,
        `${qKey}-12`,
        `${qKey}-13`,
        `${qKey}-14`,
        `${qKey}-15`,
        `${qKey}-16`,
        `${qKey}-17`,
        `${qKey}-18`,
        `${qKey}-19`,
      ]);

      return {
        scores: [objectiveScore, subjectiveScore],
      };
    },
  },
};
